import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { api, getBioToken } from "../api";
import { Button } from "./ui/button";
import { startRegistration } from "@simplewebauthn/browser";
import { describePasskeyError, reportPasskeyError } from "../lib/passkeyDebug";
import { toast } from "sonner";
import { Fingerprint, Loader2 } from "lucide-react";

const TOUR_PREFIX = "osc_tour_done_v1_";

/**
 * After login, checks whether this device already has a passkey (FaceID).
 * If not, offers to save one — so the next sign-in is a single FaceID tap.
 * Shown at most once per session; skipped while the welcome tour is pending.
 */
export const PasskeyPrompt = () => {
  const { user, setUser, t } = useApp();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let timer = null;
    (async () => {
      try {
        if (localStorage.getItem("pmf_has_passkey") === "1") return; // already saved on this device
        if (getBioToken()) return; // device already unlocks with native FaceID — don't nag
        if (sessionStorage.getItem("pmf_passkey_prompted") === "1") return; // once per session
        if (!localStorage.getItem(`${TOUR_PREFIX}${user.id}`)) return; // don't overlap the welcome tour
        if (!window.PublicKeyCredential) return; // platform without passkey support
        let available = true;
        try {
          if (window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) {
            available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          }
        } catch (e) { /* shim may not implement it — assume available */ }
        if (!available || cancelled) return;
        timer = setTimeout(() => {
          try { sessionStorage.setItem("pmf_passkey_prompted", "1"); } catch (e) {}
          setOpen(true);
        }, 1200);
      } catch (e) { /* never block the app */ }
    })();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [user]);

  const enable = async () => {
    setBusy(true);
    try {
      const optRes = await api.post("/auth/webauthn/register-options", {});
      const attestation = await startRegistration({ optionsJSON: optRes.data });
      const verify = await api.post("/auth/webauthn/register-verify", { credential: attestation });
      if (verify.data?.user) setUser(verify.data.user);
      try { localStorage.setItem("pmf_has_passkey", "1"); } catch (e) {}
      toast.success(t("passkey_added"));
      setOpen(false);
    } catch (e) {
      // iOS AuthorizationError 1006 (matchedExcludedCredential) / InvalidStateError:
      // this device ALREADY has a passkey for the account — that's a success, not an error
      const msg = String(e?.message || e || "");
      if (e?.name === "InvalidStateError" || /1006|matchedexcluded/i.test(msg)) {
        try { localStorage.setItem("pmf_has_passkey", "1"); } catch (err) { /* ignore */ }
        toast.success(t("passkey_already_active"));
        setOpen(false);
        return;
      }
      reportPasskeyError("passkey-prompt-register", e);
      if (e?.name !== "NotAllowedError") toast.error(`${t("passkey_error")} — ${describePasskeyError(e)}`);
    } finally {
      setBusy(false);
    }
  };

  if (!open || !user) return null;

  return (
    <div
      data-testid="passkey-prompt-overlay"
      className="fixed inset-0 z-[140] flex items-end sm:items-center justify-center bg-black/45 backdrop-blur-sm px-5"
      style={{
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)",
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
      }}
    >
      <div className="w-full max-w-sm bg-card border rounded-2xl shadow-xl p-6 text-center" data-testid="passkey-prompt-card">
        <div className="mx-auto h-14 w-14 rounded-full bg-[#2B3990]/10 flex items-center justify-center mb-3">
          <Fingerprint className="h-7 w-7 text-[#2B3990]" />
        </div>
        <h2 className="font-semibold text-base" data-testid="passkey-prompt-title">{t("passkey_prompt_title")}</h2>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{t("passkey_prompt_text")}</p>
        <div className="mt-5 space-y-2">
          <Button
            data-testid="passkey-prompt-enable-btn"
            onClick={enable}
            disabled={busy}
            className="w-full h-11 bg-[#2B3990] hover:bg-[#232e75] text-white"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Fingerprint className="h-4 w-4 mr-2" />}
            {t("passkey_prompt_enable")}
          </Button>
          <button
            type="button"
            data-testid="passkey-prompt-later-btn"
            onClick={() => setOpen(false)}
            disabled={busy}
            className="w-full text-center text-xs font-medium text-muted-foreground hover:text-foreground py-2"
          >
            {t("passkey_prompt_later")}
          </button>
        </div>
      </div>
    </div>
  );
};
